import type { Role } from "@/data/mock";

// ─────────────────────────────────────────────────────────────────
// Iratation High School OS — school identity.
//
// The one file a real deployment would edit to onboard a new
// school. Name, motto, colors, logo mark, staff roster, class
// timetable, and the family relationships used by the parent
// and student dashboards all live here.
//
// Nothing in this file is derived. If a value appears here, it
// is because a human at Iratation High School typed it in.
// Anything computed — initials, contrast colors, tints — lives
// at the point of use.
// ─────────────────────────────────────────────────────────────────

export const school = {
  // ─── Identity ─────────────────────────────────────────────
  name: "Iratation High School",
  shortName: "IHS",
  motto: "Knowledge · Integrity · Service",
  founded: 1987,
  district: "Kampala",
  country: "Uganda",

  // ─── Logo ─────────────────────────────────────────────────
  // `mark` is a single character rendered inside a navy square
  // with gold text. It appears in the login screen, the header,
  // and the Customize preview. A real deployment would swap
  // this for an SVG path, but for the demo a character is
  // enough and it lets the Customize page offer a picker.
  logo: {
    mark: "I",
    // The complete SVG mark, inlined for the favicon and any
    // place that needs the full lockup. Kept here so that
    // changing the school's brand in one place propagates.
    // Uses the navy and gold literals below rather than
    // referencing them — SVG in a data URL can't read JS.
    svg:
      "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 64 64'><rect width='64' height='64' rx='16' fill='%230A2540'/><path d='M20 18h24v6H34v22h8v6H22v-6h8V24h-10z' fill='%23F5C842'/></svg>",
  },

  // ─── Colors ───────────────────────────────────────────────
  // The two values that flow through the app as CSS custom
  // properties (--brand-navy, --brand-gold). The store seeds
  // from these on first load and signOut restores them, so
  // they are the canonical source of truth for "what color is
  // Iratation High School".
  //
  // The tints are precomputed rather than derived with
  // `color-mix()` at runtime, because our build target is
  // Safari 15.4 and `color-mix` requires Safari 16.2. Having
  // them here as named constants means the tint is reviewable
  // next to the base, not buried inside a component's style
  // prop. If navy changes, navyTint changes with it — that is
  // a manual step, but it's a visible one.
  colors: {
    navy: "#0A2540",
    gold: "#F5C842",

    // 8–10% tints of the two base colors, precomputed as
    // rgba so they compose correctly over white cards and
    // over the F2F2F7 grouped background. Used by the tab
    // bar's active pill, the row icon squares for brand
    // tone, and the Customize preview.
    navyTint: "rgba(10, 37, 64, 0.08)",
    navyTintStrong: "rgba(10, 37, 64, 0.14)",
    goldTint: "rgba(245, 200, 66, 0.14)",

    // Text and icons that sit on top of a navy surface.
    onNavy: "#FFFFFF",
    onNavyMuted: "rgba(255, 255, 255, 0.72)",
  },

  // ─── Staff and student roster ─────────────────────────────
  // The list of names the Login screen shows once a role is
  // picked. Kept deliberately short — the demo runs on an
  // iPhone SE and a roster of forty names would scroll off
  // screen. A real deployment pulls from the users table.
  //
  // Note the ordering: directors and bursars first, teachers
  // next, students and parents last. This mirrors the hierarchy
  // the app assumes — a director is the primary persona.
  roster: {
    director: ["Margaret Nakamya"],
    bursar: ["Charles Ochieng"],
    teacher: [
      "Sarah Kizza",
      "Robert Muwanga",
      "Agnes Nabirye",
    ],
    student: [
      "Amina Nakato",
      "Brian Okello",
      "Catherine Auma",
    ],
    parent: [
      "Grace Nakato",
      "Peter Okello",
      "Mary Auma",
    ],
  } as Record<Role, readonly string[]>,

  // ─── Timetable ────────────────────────────────────────────
  // Today's classes for the signed-in teacher. Used by the
  // teacher dashboard's "Today's classes" card. In a real
  // deployment this would be derived from the teacher's
  // assignments and the term's calendar; here it's static.
  timetable: [
    { id: "t1", subject: "Mathematics",  klass: "S3 Blue",  room: "Room 12", time: "08:00" },
    { id: "t2", subject: "Mathematics",  klass: "S2 Green", room: "Room 08", time: "10:40" },
    { id: "t3", subject: "Physics",      klass: "S4 Red",   room: "Lab 1",   time: "14:00" },
    { id: "t4", subject: "Mathematics",  klass: "S3 Blue",  room: "Room 12", time: "15:30" },
  ],

  // ─── Children ─────────────────────────────────────────────
  // The relationship between the signed-in parent (or student)
  // and the enrolled students they can see. Used by the family
  // block on the parent/student dashboard.
  //
  // In a real deployment this comes from the memberships table
  // — one row per (user, student) pair with a role of "parent"
  // or "self". Here, it's a fixture.
  children: [
    {
      name: "Amina Nakato",
      klass: "S3 Blue",
      status: "Paid",
      balance: "UGX 0",
    },
    {
      name: "Joel Wanyama",
      klass: "S1 Green",
      status: "Partial",
      balance: "UGX 90,000",
    },
  ],
} as const;

// ─── Derived types ──────────────────────────────────────────────
//
// Exporting the shape of `school` so components that accept
// pieces of it can type their props without importing the
// whole object. `typeof school` is fine for internal use, but
// a named type communicates intent at a prop boundary.

export type School = typeof school;
export type SchoolColors = typeof school.colors;
export type Roster = typeof school.roster;
export type TimetableEntry = (typeof school.timetable)[number];
export type Child = (typeof school.children)[number];

// ─── Convenience accessors ──────────────────────────────────────
//
// Two derived values that several files need. Exported as
// functions rather than as constants so that if the theme ever
// becomes stateful (per-tenant, loaded from an API), the call
// sites don't change.

export function getRoster(role: Role): readonly string[] {
  return school.roster[role];
}

export function formatSchoolName(): string {
  return `${school.name} · ${school.district}`;
}